import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Alert, Box, CircularProgress, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { InfoTooltip } from '@components/common/InfoTooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MedicationIcon from '@mui/icons-material/Medication';
import { ActivityDumbbellIcon, FeelingSparklesIcon, FoodAppleIcon, WaterDropCategoryIcon } from '@components/icons';
import { SectionIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { recommendationsProgress as c } from '@/styles/tokens';
import { getRecommendationsProgress } from '@services/calendar.service';
import { RecommendationCategory, RecommendationsDay, RecommendationsProgress, RecommendationsRange } from '@/types/recommendations-progress';
import { recommendationsProgressMock } from './dashboardMocks';

const RANGES: RecommendationsRange[] = [7, 14, 30];
const Y_AXIS = [100, 75, 50, 25, 0];
const CHART_HEIGHT = 202;

const CATEGORY_ICONS: Record<RecommendationCategory, typeof CheckCircleIcon> = {
  water: WaterDropCategoryIcon,
  food: FoodAppleIcon,
  activity: ActivityDumbbellIcon,
  feeling: FeelingSparklesIcon,
  medication: MedicationIcon,
  tasks: CheckCircleIcon,
};

// Bar colour by completion level; thresholds are not given in the design.
const levelColors = (percent: number) => (percent >= 60 ? c.high : percent >= 30 ? c.medium : c.low);

// Date labels are thinned out on longer periods so they do not overlap.
const dateLabelStep = (days: number) => (days <= 7 ? 1 : days <= 14 ? 2 : 5);

const DailyChart: React.FC<{ days: RecommendationsDay[] }> = ({ days }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.recommendations' });
  const dates = days.map((day) => format(parseISO(day.date), 'dd.MM'));
  const step = dateLabelStep(days.length);
  const showValues = days.length <= 14;
  const slotSx = { flex: '1 1 0', minWidth: 0, maxWidth: 36, display: 'flex', justifyContent: 'center' } as const;
  const rowSx = { display: 'flex', justifyContent: 'space-between', gap: '4px' } as const;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box sx={{ position: 'relative', width: 30, flexShrink: 0, height: CHART_HEIGHT }}>
          {Y_AXIS.map((tick) => (
            <Typography
              key={tick}
              sx={{ position: 'absolute', top: `${100 - tick}%`, right: 0, transform: 'translateY(-50%)', fontSize: 12, lineHeight: '14px', color: c.axis }}
            >
              {tick}%
            </Typography>
          ))}
        </Box>

        <Box sx={{ position: 'relative', flexGrow: 1, height: CHART_HEIGHT, borderTop: `1px solid ${c.grid}`, borderBottom: `1px solid ${c.grid}` }}>
          {[25, 50, 75].map((line) => (
            <Box key={line} sx={{ position: 'absolute', left: 0, right: 0, top: `${line}%`, borderTop: `1px solid ${c.grid}` }} />
          ))}
          <Box sx={{ ...rowSx, position: 'relative', height: '100%', px: 1.5 }}>
            {days.map((day, i) => {
              // Day without recommendations: empty track, "no data" tooltip.
              const value = day.percent;
              const colors = value === null ? null : levelColors(value);
              return (
                <Box key={day.date} sx={slotSx}>
                  <InfoTooltip
                    title={value === null ? t('noData') : `${value}%`}
                    description={value === null
                      ? dates[i]
                      : `${t('planned', { count: day.planned })}\n${t('done', { count: day.completed })}`}
                  >
                    <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: c.barTrack, borderRadius: '4px' }}>
                      {colors && (
                        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${value}%`, bgcolor: colors.fill, borderRadius: '4px' }} />
                      )}
                      {colors && showValues && (
                        <Typography
                          sx={{
                            position: 'absolute', left: 0, right: 0, bottom: `calc(${value}% + 4px)`, textAlign: 'center',
                            fontSize: 10, lineHeight: '12px', fontWeight: 500, color: colors.text, whiteSpace: 'nowrap',
                          }}
                        >
                          {value}%
                        </Typography>
                      )}
                    </Box>
                  </InfoTooltip>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Left padding = y-axis (30) + gap (8) + bars padding (12). */}
      <Box sx={{ ...rowSx, mt: 1, pl: '50px', pr: 1.5 }}>
        {dates.map((date, i) => (
          <Box key={date} sx={slotSx}>
            <Typography sx={{ fontSize: 12, lineHeight: '14px', color: c.axis, whiteSpace: 'nowrap' }}>
              {(dates.length - 1 - i) % step === 0 ? date : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Recommendations completion (Figma "Виконання рекомендацій"): this expert's calendar recommendations for the client.
 * Real data by default (the switch flips to the mock); each period is loaded only when selected and kept for the page session.
 */
export const RecommendationsProgressCard: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.recommendations' });
  const [range, setRange] = useState<RecommendationsRange>(7);
  const [real, setReal] = useState(true);
  const [loaded, setLoaded] = useState<Partial<Record<RecommendationsRange, RecommendationsProgress>>>({});
  const [error, setError] = useState(false);
  const current = real ? loaded[range] : recommendationsProgressMock(range);

  useEffect(() => setLoaded({}), [clientId]);

  useEffect(() => {
    if (!real || current) return;
    let cancelled = false;
    setError(false);
    getRecommendationsProgress(clientId, range)
      .then((data) => { if (!cancelled) setLoaded((prev) => ({ ...prev, [range]: data })); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [clientId, range, current, real]);

  return (
    <Box
      data-i-doc={SectionIndices.EXPERT_CLIENT_RECOMMENDATIONS}
      sx={{ bgcolor: c.background, boxShadow: c.shadow, borderRadius: '12px', p: 2, display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
    >
      {/* Narrow screens: the period switch goes below the average, full width with equal segments. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 14, lineHeight: '16px', color: c.label }}>{t('title')}</Typography>
            <Tooltip title={t('realDataSwitch')}>
              <Switch
                size="small"
                checked={real}
                onChange={(e) => { setReal(e.target.checked); setError(false); }}
                inputProps={{ 'aria-label': t('realDataSwitch') }}
                sx={{ my: '-6px', '& .Mui-checked': { color: c.rangeActiveText }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: c.rangeActiveText } }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 22, lineHeight: '26px', fontWeight: 600, color: c.value }}>{current?.average != null ? `${current.average}%` : '—'}</Typography>
            <Box sx={{ bgcolor: c.pillBg, borderRadius: '20px', px: 1.5, py: '6px' }}>
              <Typography sx={{ fontSize: 12, lineHeight: '14px', fontWeight: 600, color: c.pillText }}>{t('average', { count: range })}</Typography>
            </Box>
          </Box>
        </Box>

        <ToggleButtonGroup
          exclusive
          value={range}
          onChange={(_, value: RecommendationsRange | null) => value && setRange(value)}
          aria-label={t('period')}
          sx={{ height: 32, width: { xs: '100%', sm: 'auto' }, bgcolor: c.rangeBg, borderRadius: '4px', gap: '2px' }}
        >
          {RANGES.map((value) => (
            <ToggleButton
              key={value}
              value={value}
              sx={{
                flex: { xs: 1, sm: 'none' }, px: 1.5, py: 0, textTransform: 'none', fontSize: 12, lineHeight: '14px', fontWeight: 400,
                color: c.label, bgcolor: c.rangeItemBg, border: '1px solid transparent', borderRadius: '3px !important',
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: c.rangeActiveBg, color: c.rangeActiveText, fontWeight: 500,
                  border: `1px solid ${c.rangeActiveBorder} !important`, borderRadius: '4px !important',
                },
              }}
            >
              {t('range', { count: value })}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {real && error ? (
        <Alert severity="warning">{t('loadError')}</Alert>
      ) : !current ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: 14, lineHeight: '17px', color: c.label }}>{t('byDays')}</Typography>
            <DailyChart days={current.daily} />
          </Box>

          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: 14, lineHeight: '17px', color: c.label }}>{t('byCategories')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {current.categories.map(({ category, percent }) => {
                const Icon = CATEGORY_ICONS[category];
                const colors = c.categories[category];
                return (
                  <Box
                    key={category}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 40, px: 1, py: '6px', bgcolor: c.itemBg, borderRadius: '8px' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon sx={{ fontSize: 16, color: colors.icon }} />
                      </Box>
                      <Typography sx={{ fontSize: 14, lineHeight: '17px', color: c.text }}>{t(`categories.${category}`)}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, lineHeight: '17px', fontWeight: 500, color: c.text, textAlign: 'right' }}>{percent === null ? '—' : `${percent}%`}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
